import { isErrorLike } from '@apollo/client/errors';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { PageLoader } from '@optools/components/loading/page-loader';
import { displayApolloError } from '@optools/components/notifications/apollo';
import { USER_DATA, USER_ID } from '@optools/services/operator-gateway/user-lookup/queries';
import { ExternalLogs } from '@optools/tools/user-lookup/components/external-logs';
import { SearchForm } from '@optools/tools/user-lookup/components/search-form';
import { SessionsTable } from '@optools/tools/user-lookup/components/sessions-table';
import UserInfoTable from '@optools/tools/user-lookup/components/user-info-table';
import { UserOverrides } from '@optools/tools/user-lookup/components/user-overrides';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  emailSchema,
  FormValues,
  QueryParams,
  querySchema,
} from '@optools/tools/user-lookup/schemas';
import { formatYearMonthDay } from '@optools/utils/datetime';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/user-lookup/')({
  component: RouteComponent,
  validateSearch: zodValidator(querySchema),
  search: {
    middlewares: [
      stripSearchParams({
        from: '',
        id: '',
        page: DEFAULT_PAGE_INDEX,
        size: DEFAULT_PAGE_SIZE,
        to: '',
        email: '',
        sessionId: '',
      }),
    ],
  },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const searchParams = useSearch({ from: '/_auth/user-lookup/' });
  const navigate = useNavigate({ from: Route.fullPath });

  const {
    email,
    from: fromDate,
    id: userId,
    page: pageIndex,
    sessionId,
    size: pageSize,
    to: toDate,
  } = searchParams;

  const [getUserInfo, { loading: loadingUserInfo }] = useLazyQuery(USER_ID, {
    fetchPolicy: 'network-only',
  });

  const {
    client,
    data,
    dataState,
    loading: loadingUserData,
    previousData,
    refetch,
  } = useQuery(USER_DATA, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    skip: !userId,
    variables: { endDate: toDate, page: pageIndex, size: pageSize, startDate: fromDate, userId },
  });

  const handleUpdateSearchParams = useCallback(
    (params: Partial<QueryParams>) => {
      navigate({
        search: (prevParams) => {
          const newParams = { ...prevParams, ...params };
          if (
            prevParams.from !== newParams.from ||
            prevParams.to !== newParams.to ||
            prevParams.size !== newParams.size
          ) {
            newParams.page = 0;
          }

          return { ...prevParams, ...newParams };
        },
      });
    },
    [navigate],
  );

  const clearUserData = () => {
    client.clearStore();
    handleUpdateSearchParams({ email: '', id: '' });
  };

  useEffect(() => {
    if (dataState === 'complete' && !data?.userData && userId) {
      toast.error('User Data Not Found', {
        description: `User data could not be found for ${userId}`,
      });
    }
  }, [data, dataState, userId]);

  async function handleSubmit({ searchDates, searchValue }: FormValues) {
    const params: Pick<typeof searchParams, 'id' | 'from' | 'to' | 'email'> = {
      email: '',
      from: '',
      id: '',
      to: '',
    };

    if (searchValue && emailSchema.safeParse(searchValue).success) {
      const { data, error } = await getUserInfo({
        context: { skipErrorNotification: true },
        variables: { email: searchValue },
      });

      const id = data?.userInfo?.id ?? '';
      params.email = searchValue;
      params.id = id;

      if (isErrorLike(error) && !data) {
        displayApolloError({ error, title: 'User Info Error' });
        clearUserData();
      } else if (!id) {
        toast.error('User Not Found', {
          description: 'The email provided does not match any existing users',
        });
        clearUserData();
      }
    } else {
      params.email = '';
      params.id = searchValue;
    }

    const from = searchDates?.from && formatYearMonthDay(searchDates.from);
    const to = searchDates?.to && formatYearMonthDay(searchDates.to);

    if (from && to) {
      params.from = from;
      params.to = to;
    }

    // Check if nothing has changed, if so refetch
    if (searchValue === userId && fromDate === from && toDate === to) {
      refetch();
    }

    handleUpdateSearchParams(params);
  }

  const isNewQuery = previousData?.userData.userId !== userId;
  const userData = data?.userData ?? (!isNewQuery ? previousData?.userData : null);
  const sessions = userData?.sessions?.sessions ?? [];
  const total = userData?.sessions?.totalCount ?? 0;

  return (
    <div className="space-y-6">
      <SearchForm
        email={email}
        fromDate={fromDate}
        loading={loadingUserData || loadingUserInfo}
        toDate={toDate}
        userId={userId}
        onSubmit={handleSubmit}
      />
      {loadingUserData && isNewQuery ? (
        <PageLoader />
      ) : (
        userData &&
        userId && (
          <>
            {userId && <ExternalLogs userId={userId} />}
            {userData && (
              <UserInfoTable
                cachedEntitlements={userData.cachedEntitlements}
                cachedProfile={userData.cachedProfile}
                entitlements={userData.entitlements}
                profile={userData.profile}
                userId={userId}
              />
            )}
            {userId && <UserOverrides userId={userId} />}
            {userData && (
              <SessionsTable
                pagination={{ pageIndex, pageSize }}
                selectedSessionId={sessionId}
                sessions={sessions}
                total={total}
                onUpdateSearchParams={handleUpdateSearchParams}
              />
            )}
          </>
        )
      )}
    </div>
  );
}
